import axiosInstance from "utils/axios";
import { toast } from "sonner";

// Get all teams
export const getAllTeams = async (params = {}) => {
  try {

    const requestBody = {
      search: params.search || "",
      pageNumber: params.pageNumber || 1,
      pageSize: params.pageSize || 100,
      teamTypeId: params.teamTypeId ? params.teamTypeId : params?.counsellorsOnly ? "05640150-1FA0-407C-9FE2-6EB7A82CFC77" : null,
      sortColumn: params.sortColumn || null,
      sortDirection: params.sortDirection || null,
    };

    const response = await axiosInstance.post("/api/Teams/get-teams", requestBody);


    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      // Parse the data field if it's a string
      let teamsData = response.data.data;
      if (typeof teamsData === "string") {
        try {
          teamsData = JSON.parse(teamsData);
        } catch (parseError) {
          console.error("Error parsing teams data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      if (!Array.isArray(teamsData)) {
        throw new Error("Expected teams data to be an array");
      }

      // Transform the API data to match the table format
      const transformedTeams = teamsData.map((team) => ({
        id: team.TeamId,
        teamId: team.TeamId,
        teamName: team.TeamName,
        description: team.Description,
        isActive: team.IsActive,
        createdOn: team.CreatedAt,
        createdBy: team.CreatedBy,
        modifiedAt: team.ModifiedAt,
        modifiedBy: team.ModifiedBy,
        companyId: team.CompanyId,
        memberCount: team.MemberCount || 0,
        members: team.Members || [],
        userIds: team.UserIds || [],
        teamTypeId: team.TeamTypeId,
      }));

      return {
        success: true,
        data: transformedTeams,
        totalRecords: response.data.totalRecords || teamsData.length,
        message: response.data.message || "Teams fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch teams");
    }
  } catch (error) {
    console.error("Error fetching teams:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch teams";

    toast.error("Failed to load teams", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
      data: [],
      totalRecords: 0,
    };
  }
};

// Get team by ID
export const getTeamById = async (teamId) => {
  try {
    const response = await axiosInstance.get(`/api/Teams/${teamId}`);

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Team fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch team");
    }
  } catch (error) {
    console.error("Error fetching team:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to fetch team";

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Create a new team
export const createTeam = async (teamData) => {
  try {

    const requestBody = {
      teamName: teamData.teamName,
      description: teamData.description,
      userIds: teamData.userIds || [],
      isActive: teamData.isActive !== undefined ? teamData.isActive : true,
      teamTypeId: teamData.teamTypeId || null,
    };

    const response = await axiosInstance.post("/api/Teams", requestBody);


    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success("Team created successfully", {
        description: "The new team has been added to the system.",
      });
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Team created successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to create team");
    }
  } catch (error) {
    console.error("Error creating team:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to create team";

    toast.error("Failed to create team", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Update team
export const updateTeam = async (teamId, teamData) => {
  try {
    const requestBody = {
      teamId: teamId,
      teamName: teamData.teamName,
      description: teamData.description,
      userIds: teamData.userIds || [],
      isActive: teamData.isActive !== undefined ? teamData.isActive : true,
      teamTypeId: teamData.teamTypeId || null,
    };

    const response = await axiosInstance.put(
      `/api/Teams/${teamId}`,
      requestBody,
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success("Team updated successfully");
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Team updated successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to update team");
    }
  } catch (error) {
    console.error("Error updating team:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to update team";

    toast.error("Failed to update team", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Get team members
export const getTeamMembers = async (teamId) => {
  try {

    const response = await axiosInstance.get(`/api/Teams/get-members/${teamId}`);


    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      // Parse the data field if it's a string
      let membersData = response.data.data;
      if (typeof membersData === "string") {
        try {
          membersData = JSON.parse(membersData);
        } catch (parseError) {
          console.error("Error parsing members data:", parseError);
          throw new Error("Invalid data format received from server");
        }
      }

      if (!Array.isArray(membersData)) {
        membersData = [];
      }

      // Transform the data
      const transformedMembers = membersData.map((member) => ({
        teamMemberId: member.TeamMemberId,
        userId: member.UserId,
        fullName: member.FullName,
        createdAt: member.CreatedAt,
      }));

      return {
        success: true,
        data: transformedMembers,
        message: response.data.message || "Team members fetched successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to fetch team members");
    }
  } catch (error) {
    console.error("Error fetching team members:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to fetch team members";

    return {
      success: false,
      error: errorMessage,
      data: [],
    };
  }
};

// Add members to team
export const addTeamMembers = async (teamId, userIds) => {
  try {

    const requestBody = {
      teamId: teamId,
      userIds: userIds || [],
    };

    const response = await axiosInstance.post(
      `/api/Teams/${teamId}/members`,
      requestBody,
    );


    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success("Team members added successfully");
      return {
        success: true,
        data: response.data.data,
        message: response.data.message || "Team members added successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to add team members");
    }
  } catch (error) {
    console.error("Error adding team members:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to add team members";

    toast.error("Failed to add team members", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Delete team
export const deleteTeam = async (teamId) => {
  try {
    const response = await axiosInstance.delete(`/api/Teams/${teamId}`);

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success("Team deleted successfully");
      return {
        success: true,
        message: response.data.message || "Team deleted successfully",
      };
    } else {
      throw new Error(response.data.message || "Failed to delete team");
    }
  } catch (error) {
    console.error("Error deleting team:", error);

    const errorMessage =
      error.response?.data?.message || error.message || "Failed to delete team";

    toast.error("Failed to delete team", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};

// Toggle team status (activate/deactivate)
export const toggleTeamStatus = async (teamId, isActive) => {
  try {
    const response = await axiosInstance.patch(
      `/api/Teams/ToggleStatus/${teamId}`,
      { isActive },
    );

    if (
      response.data.statusCode === 200 &&
      response.data.status === "Success"
    ) {
      toast.success(
        `Team ${isActive ? "activated" : "deactivated"} successfully`,
      );
      return {
        success: true,
        message:
          response.data.message ||
          `Team ${isActive ? "activated" : "deactivated"} successfully`,
      };
    } else {
      throw new Error(
        response.data.message || "Failed to toggle team status",
      );
    }
  } catch (error) {
    console.error("Error toggling team status:", error);

    const errorMessage =
      error.response?.data?.message ||
      error.message ||
      "Failed to toggle team status";

    toast.error("Failed to update team status", {
      description: errorMessage,
      duration: 5000,
    });

    return {
      success: false,
      error: errorMessage,
    };
  }
};


export const getTeamTypes = async () => {
  try {
    const response = await axiosInstance.get("/api/Teams/get-team-types");
    const resData = response.data;

    let parsedData = resData?.data;

    // Parse data if it's a JSON string
    try {
      if (typeof parsedData === "string") {
        parsedData = JSON.parse(parsedData);
      }
    } catch (parseError) {
      console.error("Error parsing team types data:", parseError);
    }

    return {
      ...resData,
      data: parsedData,
    };

  } catch (error) {
    console.error("Error fetching team types:", error);

    const errData = error.response?.data;

    let parsedErrorData = errData?.data;
    try {
      if (typeof parsedErrorData === "string") {
        parsedErrorData = JSON.parse(parsedErrorData);
      }
    } catch {
      // ignore JSON parse errors
    }

    return {
      message: errData?.message || "Something went wrong while fetching team types",
      statusCode: errData?.statusCode || 500,
      status: errData?.status || "Error",
      data: parsedErrorData || null,
      totalRecords: errData?.totalRecords || 0,
    };
  }
};

export const getUsersForTeamType = async (teamTypeCode) => {
  try {
    const response = await axiosInstance.get(
      `/api/Teams/get-users-for-team-type`,
      {
        params: { TeamTypeCode: teamTypeCode ?? "" },
      }
    );

    const resData = response.data;
    let parsedData = resData?.data;

    // Parse data only if it is string
    if (typeof parsedData === "string") {
      try {
        parsedData = JSON.parse(parsedData);
      } catch (error) {
        console.error("Error parsing users data:", error);
      }
    }

    return {
      success: resData?.statusCode === 200,
      message: resData?.message ?? "",
      statusCode: resData?.statusCode ?? 200,
      status: resData?.status ?? "Success",
      data: parsedData ?? [],
      totalRecords: resData?.totalRecords ?? 0,
    };

  } catch (error) {
    const errData = error.response?.data ?? {};
    let parsedErrorData = errData?.data;

    if (typeof parsedErrorData === "string") {
      try {
        parsedErrorData = JSON.parse(parsedErrorData);
      } catch {
        // ignore parsing error
      }
    }

    return {
      success: false,
      message: errData?.message || "Something went wrong while fetching users",
      statusCode: errData?.statusCode || 500,
      status: errData?.status || "Error",
      data: parsedErrorData || [],
      totalRecords: errData?.totalRecords || 0,
    };
  }
};

export const getTeamsByTeamTypeCode = async (teamTypeCode) => {
  try {
    const response = await axiosInstance.get(
      `/api/Teams/get-teams-by-team-type-code`,
      {
        params: { TeamTypeCode: teamTypeCode ?? "" },
      }
    );

    const resData = response.data;
    let parsedData = resData?.data;

    // Parse data only if it is string
    if (typeof parsedData === "string") {
      try {
        parsedData = JSON.parse(parsedData);
      } catch (error) {
        console.error("Error parsing teams data:", error);
      }
    }

    return {
      success: resData?.statusCode === 200,
      message: resData?.message ?? "",
      statusCode: resData?.statusCode ?? 200,
      status: resData?.status ?? "Success",
      data: parsedData ?? [],
      totalRecords: resData?.totalRecords ?? 0,
    };
  } catch (error) {
    const errData = error.response?.data ?? {};
    let parsedErrorData = errData?.data;

    // Parse error data only if it is string
    if (typeof parsedErrorData === "string") {
      try {
        parsedErrorData = JSON.parse(parsedErrorData);
      } catch {
        // ignore parsing error
      }
    }

    return {
      success: false,
      message:
        errData?.message ||
        "Something went wrong while fetching teams",
      statusCode: errData?.statusCode || 500,
      status: errData?.status || "Error",
      data: parsedErrorData || [],
      totalRecords: errData?.totalRecords || 0,
    };
  }
};
